import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { NumericSpinnerInput } from '@/components/inputs/NumericSpinnerInput'
import styles from './ScopeChangeEditInventoryItemsDialog.module.css'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Tooltip } from '@/components/Tooltip'
import { type Id } from '@/infra/id'

export interface ScopeChangeEditInventoryItemsDialogProps {
  showing: boolean
  onClose: () => unknown
  onAddItems: () => unknown
  onSave: () => unknown
  isSavePending: boolean
  /**
   * An `EditScopeChangeInventoryItem` per service item.
   */
  children?: ReactNode
  addItemsDialog?: ReactNode
}

export function ScopeChangeEditInventoryItemsDialog({
  showing,
  onClose,
  onAddItems,
  onSave,
  isSavePending,
  children,
  addItemsDialog,
}: ScopeChangeEditInventoryItemsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSavePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle>Edit service items</ModalSheetTitle>
      </ModalSheetHeader>
      <InventoryItemHeadings />
      <ModalSheetContent>
        <div className={styles.itemsContainer}>
          {children}
          <div className={styles.addItemContainer}>
            <div className={styles.itemText}>Need more service items?</div>
            <Button
              label="Add more"
              variant="text"
              testId="edit-inventory-scope-changes-add-button"
              onPress={onAddItems}
            />
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSavePending}
            onPress={onClose}
          />
          <Button
            label="Save"
            onPress={onSave}
            variant="primary"
            testId="scope-change-request-edit"
            isPending={isSavePending}
            isDisabled={isSavePending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>

      {addItemsDialog}
    </ModalSheet>
  )
}

function InventoryItemHeadings() {
  return (
    <div className={styles.subheading}>
      <span>Service items</span>

      <div className={styles.subheadingCurrentContainer}>
        <span className={styles.subheadingCurrent}>Current</span>
        <Tooltip
          content={'Current quantity for each item on the job.'}
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </div>
      <div className={styles.subheadingNewTotalContainer}>
        <span className={styles.subheadingNewTotal}>New total</span>
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </div>
    </div>
  )
}

export interface EditScopeChangeInventoryItemProps {
  versionId: Id
  name: string
  currentQuantity: number
  newQuantity: number
  newQuantityError?: string
  onQuantityChange: (quantity: number | null) => unknown
  onIncrementQuantity: () => unknown
  onDecrementQuantity: () => unknown
  onRemove: () => unknown
}

export function EditScopeChangeInventoryItem({
  versionId,
  name,
  currentQuantity,
  newQuantity,
  newQuantityError,
  onQuantityChange,
  onIncrementQuantity,
  onDecrementQuantity,
  onRemove,
}: EditScopeChangeInventoryItemProps) {
  return (
    <div className={styles.itemContainer}>
      <div className={styles.textContainer}>
        <div className={styles.title}>{name}</div>
      </div>

      <div className={styles.currentQuantityContainer}>{currentQuantity}</div>

      <NumericSpinnerInput
        min={0}
        value={newQuantity}
        onChange={onQuantityChange}
        onIncrement={onIncrementQuantity}
        onDecrement={onDecrementQuantity}
        invalid={Boolean(newQuantityError)}
        testId={`scope-change-inventory-quantity-${versionId}`}
      />

      <IconButton
        name="close"
        label="Remove item"
        className={styles.removeItemButton}
        onPress={onRemove}
        testId={`request-scope-changes-inventory-remove-button-${versionId}`}
        size={24}
      />
    </div>
  )
}
